import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import type { AgentDefinitionDto } from '../../types/agent';
import type { CreateSessionRequest } from '../../types/session';
import { ParameterForm } from './ParameterForm';
import { agentService } from '../../services/agentService';
import { useContextStore } from '../../store/contextStore';

interface CreateSessionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSessionRequest) => Promise<void>;
}

/**
 * Dialog component for creating a new agent session.
 */
export const CreateSessionDialog = ({ open, onClose, onSubmit }: CreateSessionDialogProps) => {
  const [sessionName, setSessionName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agents, setAgents] = useState<AgentDefinitionDto[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinitionDto | null>(null);
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [initialMessage, setInitialMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const scopeLabel = currentTenant
    ? currentProject
      ? `Agents for ${currentTenant.name} / ${currentProject.name}`
      : `Agents for ${currentTenant.name} (tenant-level)`
    : 'Agents';

  useEffect(() => {
    if (open) {
      loadAgents();
    } else {
      // Reset form when dialog closes
      setSessionName('');
      setAgentId('');
      setSelectedAgent(null);
      setParameters({});
      setInitialMessage('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!agentId) {
      setSelectedAgent(null);
      setParameters({});
      return;
    }

    let cancelled = false;

    agentService.getAgent(agentId).then((fullAgent) => {
      if (cancelled) return;
      setSelectedAgent(fullAgent);
      const defaultParams: Record<string, any> = {};
      fullAgent.parameters?.forEach((param) => {
        if (param.defaultValue) {
          try {
            if (param.type === 'boolean') {
              defaultParams[param.key] = param.defaultValue === 'true';
            } else if (param.type === 'number' || param.type === 'int' || param.type === 'integer') {
              defaultParams[param.key] = Number(param.defaultValue);
            } else if (param.type === 'json' || param.type === 'object' || param.type === 'array') {
              defaultParams[param.key] = param.defaultValue;
            } else {
              defaultParams[param.key] = param.defaultValue;
            }
          } catch {
            defaultParams[param.key] = param.defaultValue;
          }
        }
      });
      setParameters(defaultParams);
    }).catch(() => {
      if (cancelled) return;
      const agent = agents.find((a) => a.id === agentId);
      setSelectedAgent(agent || null);
    });

    return () => { cancelled = true; };
  }, [agentId]);

  const loadAgents = async () => {
    setLoadingAgents(true);
    try {
      const result = await agentService.getAgents({ isActive: true });
      setAgents(result.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load agents');
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleSubmit = async () => {
    setError(null);

    if (!agentId) {
      setError('Please select an agent');
      return;
    }

    // Validate required parameters
    if (selectedAgent) {
      const missingParams = selectedAgent.parameters
        ?.filter((p) => p.isRequired && !parameters[p.key])
        .map((p) => p.label || p.key);
      if (missingParams && missingParams.length > 0) {
        setError(`Missing required parameters: ${missingParams.join(', ')}`);
        return;
      }
    }

    setLoading(true);
    try {
      const request: CreateSessionRequest = {
        agentDefinitionId: agentId,
        sessionName: sessionName.trim(),
        parameters: Object.keys(parameters).length > 0 ? parameters : undefined,
        initialMessage: initialMessage.trim() || undefined,
      };
      // When the user does not provide a session name, omit it so the
      // backend defaults it to the new session's identifier.
      if (!request.sessionName) {
        delete (request as Partial<CreateSessionRequest>).sessionName;
      }
      await onSubmit(request);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Session</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Session Name (Optional)"
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            disabled={loading}
            helperText="Leave blank to use the session ID as the name"
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {scopeLabel}
          </Typography>
          <FormControl fullWidth required disabled={loading || loadingAgents}>
            <InputLabel>Agent</InputLabel>
            <Select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              label="Agent"
            >
              {loadingAgents ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {selectedAgent && selectedAgent.parameters && selectedAgent.parameters.length > 0 && (
            <ParameterForm
              parameters={selectedAgent.parameters}
              values={parameters}
              onChange={setParameters}
            />
          )}

          <TextField
            fullWidth
            label="Initial Message (Optional)"
            value={initialMessage}
            onChange={(e) => setInitialMessage(e.target.value)}
            multiline
            rows={3}
            disabled={loading}
            placeholder="Enter an initial message to start the conversation..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Create Session'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
